import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { RefreshCw, AlertTriangle, Check, X, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface DetectedSubscription {
  id: string;
  user_id: string;
  name: string;
  amount: number;
  frequency: string;
  last_detected_date: string;
  is_confirmed: boolean;
  is_cancelled: boolean;
  category: string | null;
  notes: string | null;
}

interface Transaction {
  id: string;
  description: string | null;
  amount: number;
  category: string;
  date: string;
  type: string;
}

export const SubscriptionDetector = () => {
  const [subscriptions, setSubscriptions] = useState<DetectedSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const { toast } = useToast();
  const { formatCurrency } = useUserPreferences();

  const fetchSubscriptions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("detected_subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .order("amount", { ascending: false });

      if (error) throw error;
      setSubscriptions(data || []);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const detectSubscriptions = async () => {
    setScanning(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Fetch all transactions
      const { data: transactions, error } = await supabase
        .from("transactions")
        .select("*")
        .eq("user_id", user.id)
        .eq("type", "expense")
        .order("date", { ascending: false });

      if (error) throw error;

      // Group transactions by description and look for patterns
      const descriptionGroups: Record<string, Transaction[]> = {};
      (transactions || []).forEach((t: Transaction) => {
        if (t.description) {
          const key = t.description.toLowerCase().trim();
          if (!descriptionGroups[key]) descriptionGroups[key] = [];
          descriptionGroups[key].push(t);
        }
      });

      // Find recurring patterns (same description, similar amount, appears 2+ times)
      const potentialSubs: { name: string; amount: number; lastDate: string }[] = [];
      
      Object.entries(descriptionGroups).forEach(([desc, txs]) => {
        if (txs.length >= 2) {
          // Check if amounts are similar (within 10%)
          const amounts = txs.map(t => Number(t.amount));
          const avgAmount = amounts.reduce((a, b) => a + b, 0) / amounts.length;
          const allSimilar = amounts.every(a => Math.abs(a - avgAmount) / avgAmount < 0.1);
          
          if (allSimilar) {
            potentialSubs.push({
              name: txs[0].description || desc,
              amount: avgAmount,
              lastDate: txs[0].date,
            });
          }
        }
      });

      // Also check recurring_transactions table
      const { data: recurringTxs } = await supabase
        .from("recurring_transactions")
        .select("*")
        .eq("user_id", user.id)
        .eq("type", "expense")
        .eq("is_active", true);

      (recurringTxs || []).forEach((rt: any) => {
        if (!potentialSubs.find(s => s.name.toLowerCase() === (rt.description || rt.category).toLowerCase())) {
          potentialSubs.push({
            name: rt.description || rt.category,
            amount: Number(rt.amount),
            lastDate: rt.next_occurrence,
          });
        }
      });

      // Save new detections
      for (const sub of potentialSubs) {
        const existing = subscriptions.find(
          s => s.name.toLowerCase() === sub.name.toLowerCase()
        );
        
        if (!existing) {
          await supabase.from("detected_subscriptions").insert({
            user_id: user.id,
            name: sub.name,
            amount: sub.amount,
            last_detected_date: sub.lastDate,
            frequency: "monthly",
          });
        }
      }

      toast({ 
        title: "Scan complete! 🔍", 
        description: `Found ${potentialSubs.length} potential recurring charges` 
      });
      fetchSubscriptions();
    } catch (error) {
      console.error("Error detecting subscriptions:", error);
      toast({ title: "Error", description: "Failed to scan transactions", variant: "destructive" });
    } finally {
      setScanning(false);
    }
  };

  const markAsConfirmed = async (id: string) => {
    try {
      await supabase
        .from("detected_subscriptions")
        .update({ is_confirmed: true })
        .eq("id", id);
      fetchSubscriptions();
    } catch (error) {
      console.error("Error confirming subscription:", error);
    }
  };

  const markAsCancelled = async (id: string) => {
    try {
      await supabase
        .from("detected_subscriptions")
        .update({ is_cancelled: true })
        .eq("id", id);
      toast({ title: "Marked as cancelled 🎉", description: "Good job reviewing your subscriptions!" });
      fetchSubscriptions();
    } catch (error) {
      console.error("Error marking as cancelled:", error);
    }
  };

  const dismiss = async (id: string) => {
    try {
      await supabase
        .from("detected_subscriptions")
        .delete()
        .eq("id", id);
      fetchSubscriptions();
    } catch (error) {
      console.error("Error dismissing subscription:", error);
    }
  };

  const activeSubscriptions = subscriptions.filter(s => !s.is_cancelled);
  const monthlyTotal = activeSubscriptions.reduce((sum, s) => sum + Number(s.amount), 0);
  const unconfirmed = activeSubscriptions.filter(s => !s.is_confirmed);

  return (
    <Card className="relative overflow-hidden group transition-all duration-300 hover:shadow-lg border-border/40">
      {/* Gradient top border on hover */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <CardHeader className="pb-3 pt-5">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <div className="p-2 bg-cyan/10 rounded-lg">
                <CreditCard className="h-4 w-4 text-cyan" />
              </div>
              Subscription Tracker
            </CardTitle>
            <CardDescription className="text-sm">
              Detect and manage recurring charges
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={detectSubscriptions}
            disabled={scanning}
            className="border-cyan/30 hover:border-cyan/50 hover:bg-cyan/10"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${scanning ? "animate-spin" : ""}`} />
            Scan
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeSubscriptions.length > 0 && (
          <div className="flex justify-between items-center p-4 bg-gradient-to-r from-muted/50 to-muted/30 rounded-xl border border-border/30">
            <div>
              <p className="text-sm font-semibold">Monthly Recurring</p>
              <p className="text-xs text-muted-foreground">{activeSubscriptions.length} active subscriptions</p>
            </div>
            <p className="text-xl font-bold font-mono-numbers text-expense-text">{formatCurrency(monthlyTotal)}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
          </div>
        )}

        {unconfirmed.length > 0 && (
          <div className="flex items-center gap-3 p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <p className="text-sm">{unconfirmed.length} unreviewed subscription(s) detected</p>
          </div>
        )}

        <AnimatePresence>
          {activeSubscriptions.map((sub) => (
            <motion.div
              key={sub.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-center justify-between p-4 bg-muted/20 rounded-xl border border-border/30 hover:border-primary/30 transition-all duration-200"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold">{sub.name}</p>
                  {!sub.is_confirmed && (
                    <Badge variant="outline" className="text-xs border-cyan/30 text-cyan">New</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground capitalize">{sub.frequency}</p>
              </div>
              <p className="text-sm font-bold font-mono-numbers mr-3">{formatCurrency(Number(sub.amount))}</p>
              <div className="flex gap-1">
                {!sub.is_confirmed && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-income-text hover:text-income-text"
                    onClick={() => markAsConfirmed(sub.id)}
                    title="Confirm subscription"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => markAsCancelled(sub.id)}
                  title="Mark as cancelled"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {activeSubscriptions.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan/10 to-primary/10 flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-cyan" />
            </div>
            <p className="text-sm font-medium">No subscriptions detected yet</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3 border-cyan/30 hover:border-cyan/50" 
              onClick={detectSubscriptions}
            >
              Scan Transactions
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
